import i18n from "./index";
import locale from "@/utils/locale";
import type { SupportedLanguage } from "./i18n.types";

/**
 * 非 React 组件中使用的翻译函数
 * 优先使用 i18next，fallback 到 locale.ts
 */
export const translate = (key: string, params?: any): string => {
  // 检查 i18next 中是否存在该 key
  if (i18n.exists(key)) {
    return String(i18n.t(key, params));
  }

  // i18next 中不存在，尝试使用 locale.ts
  const localeValue = locale.get(key, params);
  if (localeValue && localeValue !== key) {
    return localeValue;
  }

  // 都没找到，返回原始 key
  return key;
};

/**
 * 简写的翻译函数
 */
export const t = translate;

/**
 * 切换语言
 */
export const changeLanguage = async (
  lang: SupportedLanguage
): Promise<boolean> => {
  try {
    await i18n.changeLanguage(lang);
    return true;
  } catch (error) {
    console.error("Failed to change language:", error);
    return false;
  }
};

/**
 * 获取当前语言
 */
export const getCurrentLanguage = (): SupportedLanguage => {
  return i18n.language as SupportedLanguage;
};

/**
 * 检查是否是支持的语言
 */
export const isSupportedLanguage = (
  lang: string
): lang is SupportedLanguage => {
  return ["en", "pt", "es"].includes(lang);
};

/**
 * 预加载语言资源
 */
export const preloadLanguage = async (
  lang: SupportedLanguage
): Promise<void> => {
  try {
    await i18n.loadLanguages(lang);
  } catch (error) {
    console.error(`Failed to preload language ${lang}:`, error);
  }
};

/**
 * 批量预加载多个语言
 */
export const preloadLanguages = async (
  langs: SupportedLanguage[]
): Promise<void> => {
  try {
    await Promise.all(langs.map(lang => i18n.loadLanguages(lang)));
  } catch (error) {
    console.error("Failed to preload languages:", error);
  }
};
